import React, { useEffect, useState } from 'react';
import { Code, Zap, Brain, Cloud, Smartphone, Database } from 'lucide-react';

const Services: React.FC = () => {
  const [isDarkMode, setIsDarkMode] = useState(false);

  useEffect(() => {
    const checkDarkMode = () => {
      const isDark = document.documentElement.classList.contains('dark');
      setIsDarkMode(isDark);
    };
    
    checkDarkMode();
    
    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });
    
    return () => observer.disconnect();
  }, []);

  const services = [
    {
      icon: <Zap size={48} />,
      title: 'IT Automation',
      description: 'Streamline your business processes with intelligent automation solutions that reduce manual work and increase efficiency.',
      features: ['Process Automation', 'Workflow Optimization', 'System Integration', 'Performance Monitoring']
    },
    {
      icon: <Code size={48} />,
      title: 'Web Development',
      description: 'Custom web applications built with modern technologies to deliver exceptional user experiences and robust functionality.',
      features: ['React/Angular Development', 'Full-Stack Solutions', 'E-commerce Platforms', 'CMS Development']
    },
    {
      icon: <Smartphone size={48} />,
      title: 'Mobile App Development',
      description: 'Native and cross-platform mobile applications that engage users and drive business growth.',
      features: ['iOS & Android Apps', 'Cross-Platform Solutions', 'UI/UX Design', 'App Store Optimization']
    },
    {
      icon: <Brain size={48} />,
      title: 'AI Solutions',
      description: 'Harness the power of artificial intelligence to automate decisions, predict outcomes, and enhance user experiences.',
      features: ['Machine Learning Models', 'Natural Language Processing', 'Computer Vision', 'Predictive Analytics']
    },
    {
      icon: <Cloud size={48} />,
      title: 'Cloud Services',
      description: 'Scalable cloud infrastructure and migration services to ensure your applications perform at their best.',
      features: ['Cloud Migration', 'Infrastructure Setup', 'DevOps Implementation', 'Security & Compliance']
    },
    {
      icon: <Database size={48} />,
      title: 'Data Analytics',
      description: 'Transform your data into actionable insights with advanced analytics and business intelligence solutions.',
      features: ['Data Visualization', 'Business Intelligence', 'Real-time Analytics', 'Custom Dashboards']
    }
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-primary-600 to-primary-800 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Our Services</h1>
            <p className="text-xl text-primary-100 max-w-3xl mx-auto">
              Comprehensive technology solutions designed to accelerate your business growth 
              and digital transformation journey.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white dark:from-gray-900 dark:to-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div 
                key={index} 
                className="relative p-8 rounded-2xl overflow-hidden backdrop-blur-lg bg-white/70 dark:bg-gray-800/50 
                         border border-white/30 dark:border-gray-700/50 shadow-xl hover:shadow-2xl transition-all duration-300
                         hover:-translate-y-1 group"
              >
                {/* Glassmorphism effect */}
                <div className="absolute inset-0 bg-gradient-to-br from-white/60 to-white/30 dark:from-gray-900/30 dark:to-gray-800/50 backdrop-blur-sm -z-10" />
                
                <div className="text-primary-600 dark:text-cyan-400 mb-4 group-hover:scale-110 transition-transform duration-300">
                  {React.cloneElement(service.icon, { size: 40 })}
                </div>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{service.title}</h3>
                <p className="text-gray-600 dark:text-gray-300 mb-6">{service.description}</p>
                <ul className="space-y-2">
                  {service.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center text-sm text-gray-600 dark:text-gray-300">
                      <div className="w-2 h-2 bg-primary-600 dark:bg-cyan-400 rounded-full mr-3 flex-shrink-0"></div>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Our Process
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300">
              A proven methodology that ensures successful project delivery
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="text-center backdrop-blur-sm bg-white/50 dark:bg-gray-800/50 p-6 rounded-2xl border border-white/30 dark:border-gray-700/50 shadow-lg">
              <div className="bg-primary-100 dark:bg-cyan-900/30 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-primary-600 dark:text-cyan-400">1</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Discovery</h3>
              <p className="text-gray-600 dark:text-gray-300">We analyze your requirements and understand your business goals</p>
            </div>
            <div className="text-center backdrop-blur-sm bg-white/50 dark:bg-gray-800/50 p-6 rounded-2xl border border-white/30 dark:border-gray-700/50 shadow-lg">
              <div className="bg-primary-100 dark:bg-cyan-900/30 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-primary-600 dark:text-cyan-400">2</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Planning</h3>
              <p className="text-gray-600 dark:text-gray-300">We create a detailed roadmap and timeline for your project</p>
            </div>
            <div className="text-center backdrop-blur-sm bg-white/50 dark:bg-gray-800/50 p-6 rounded-2xl border border-white/30 dark:border-gray-700/50 shadow-lg">
              <div className="bg-primary-100 dark:bg-cyan-900/30 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-primary-600 dark:text-cyan-400">3</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Development</h3>
              <p className="text-gray-600 dark:text-gray-300">Our expert team builds your solution using best practices</p>
            </div>
            <div className="text-center backdrop-blur-sm bg-white/50 dark:bg-gray-800/50 p-6 rounded-2xl border border-white/30 dark:border-gray-700/50 shadow-lg">
              <div className="bg-primary-100 dark:bg-cyan-900/30 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-primary-600 dark:text-cyan-400">4</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Delivery</h3>
              <p className="text-gray-600 dark:text-gray-300">We deploy and provide ongoing support for your solution</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-primary-600 to-primary-800 dark:from-cyan-700 dark:to-blue-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to Transform Your Business?
          </h2>
          <p className="text-xl text-primary-100 mb-8">
            Let's discuss how our services can help you achieve your goals
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/contact"
              className="bg-white text-primary-600 px-8 py-3 rounded-lg font-semibold hover:bg-primary-50 transition-all hover:scale-105 shadow-lg hover:shadow-xl"
            >
              Get Started Today
            </a>
            <a
              href="/contact"
              className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-primary-600 transition-all hover:scale-105 shadow-lg hover:shadow-xl"
            >
              Schedule Consultation
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;